import Shareds from '../../config/translations/Shareds';
import { hasPermissionByKeys } from '../config/Permission';
import InputTypes from '../enums/InputTypes';
import { ACTIONS } from '../enums/PermissionEnums';
import { Left } from '../utils/Global';

export default class BasePageConfig {
    #configAlterar;
    #configExcluir;
    #configExportar;
    #configGeral;
    #inputs;
    permissions = {
        [ACTIONS.INCLUIR]: false,
        [ACTIONS.EDITAR]: false,
        [ACTIONS.DELETAR]: false,
    };

    constructor(config, inputs) {
        const {
            pageUrls = '',
            legenda = '',
            pageName = '',
            customExportColumns = null,
        } = config ?? {};
        this.#configGeral = {
            pageUrl: pageUrls?.list ?? pageUrls,
            legenda: legenda ?? Shareds.legendDefault.replace('{PAGE}', pageName.toLowerCase()),
            pageName: `${pageName}`,
        };
        this.customExportColumns = customExportColumns;

        this.#configAlterar = {
            messages: {
                success: Shareds.form.successStateUpdate,
                error: Shareds.form.errorStateUpdate,
            },
        };
        this.#configExcluir = {
            messages: {
                success: Shareds.form.successDelete,
                error: Shareds.form.errorDelete,
            },
        };
        this.setInputs(inputs);
        this.setExportarConfigs();
    }

    setLegenda(legenda) {
        this.#configGeral = { ...this.#configGeral, legenda: legenda };
    }

    setInputs(inputs) {
        this.#inputs = inputs ?? [];
    }

    setIncluirPermission(routes) {
        this.permissions[ACTIONS.INCLUIR] = routes?.[ACTIONS.INCLUIR]?.permissionKeys
            ? hasPermissionByKeys(routes?.[ACTIONS.INCLUIR]?.permissionKeys)
            : true;
    }

    setEditarPermission(routes) {
        this.permissions[ACTIONS.EDITAR] = routes?.[ACTIONS.EDITAR]?.permissionKeys
            ? hasPermissionByKeys(routes?.[ACTIONS.EDITAR]?.permissionKeys)
            : true;
    }

    setConsultarPermission(routes) {
        this.permissions[ACTIONS.CONSULTAR] = routes?.[ACTIONS.CONSULTAR]?.permissionKeys
            ? hasPermissionByKeys(routes?.[ACTIONS.CONSULTAR]?.permissionKeys)
            : true;
    }

    setDeletarPermission(routes) {
        this.permissions[ACTIONS.DELETAR] = routes?.[ACTIONS.DELETAR]?.permissionKeys
            ? hasPermissionByKeys(routes?.[ACTIONS.DELETAR]?.permissionKeys)
            : true;
    }

    getAllPermissions() {
        return {
            [ACTIONS.INCLUIR]: this.permissions[ACTIONS.INCLUIR],
            [ACTIONS.EDITAR]: this.permissions[ACTIONS.EDITAR],
            [ACTIONS.DELETAR]: this.permissions[ACTIONS.DELETAR],
            [ACTIONS.CONSULTAR]: this.permissions[ACTIONS.CONSULTAR],
        };
    }

    getInputs(filterForm = false) {
        if (filterForm) return this.#inputs.filter((el) => el?.form?.use ?? false);
        return this.#inputs;
    }

    getColumnsExport(inputs = null) {
        return (inputs ? inputs : this.#inputs)
            .map((el) => {
                let exportar =
                    typeof el.export === 'object' ? el.export : { use: el.export, fromTo: [] };

                if (exportar.use) {
                    let name = el.name;
                    if (exportar?.nameUse) {
                        name = exportar.nameUse;
                    } else if (el.nameUse) {
                        name = `${el.nameUse}`;
                    } else if (el.filter?.nameUse) {
                        name = `${name}.descricao`;
                    }

                    let optionalParameter =
                        exportar?.fromTo && exportar?.fromTo.length > 0
                            ? `;${exportar.fromTo.join(',')}`
                            : '';

                    if (el?.type === InputTypes.DATE) {
                        optionalParameter = ';dd/MM/yyyy';
                    } else if (el?.type === InputTypes.DATETIME) {
                        optionalParameter = ';dd/MM/yyyy HH:mm:ss';
                    }

                    return `${name};${el['label']}${optionalParameter}`;
                }
                return false;
            })
            .filter((res) => {
                return typeof res === 'string';
            });
    }

    #getColumnsTable() {
        return this.#inputs
            .map((el) => {
                let showTable = (el?.showTable || el?.table?.use) ?? false;
                if (showTable) {
                    return {
                        nome: el?.name,
                        titulo: el?.label,
                        type: el?.type,
                    };
                }
                return false;
            })
            .filter((res) => {
                return typeof res === 'object';
            });
    }
    getInputsForm() {
        return this.#inputs
            .filter((el) => el?.form?.use)
            .reduce(function (prev, curr) {
                prev[curr?.name] =
                    curr?.form?.initialValue === undefined ? '' : curr?.form?.initialValue;
                return prev;
            }, {});
    }

    getFilterInputs() {
        return this.#inputs.filter((el) => (el?.filter && el?.filter?.use) ?? false);
    }

    getBaseColumnsLayout(columns) {
        return [
            'id;Identificador',
            ...columns,
            'contaAcessoCadastro.nome;Usuário Cadastro',
            'dataCadastro;Data de Cadastro;dd/MM/yyyy HH:mm:ss',
            'contaAcessoAtualizacao.nome;Usuário da última atualização',
            'dataAtualizacao;Data da última atualização;dd/MM/yyyy HH:mm:ss',
        ];
    }

    getBasePage(params) {
        const barra = Left(this.#configGeral.pageUrl, 1) === '/' ? '' : '/';
        let url = `${barra}${this.#configGeral.pageUrl}`;

        Object.keys(params).forEach((key) => {
            url = `${url}`.replace(`:${key}`, params[key]);
        });

        return url;
    }

    getConfigGeral() {
        return this.#configGeral;
    }

    setExportarConfigs() {
        const { pageName } = this.#configGeral;

        const columns = this.getColumnsExport();
        let customColumns = [];
        if (Array.isArray(this.customExportColumns)) {
            customColumns = this.getColumnsExport(this.customExportColumns);
        }

        this.#configExportar = {
            title: `Lista de ${pageName}`,
            messages: {
                success: Shareds.export.success,
                error: Shareds.export.error,
            },
            config: {
                pdf: {
                    columns: this.getBaseColumnsLayout([...columns, ...customColumns]),
                    properties: {
                        orientation: 'PORTRAIT',
                    },
                },
                print: {
                    columns: this.getBaseColumnsLayout([...columns, ...customColumns]),
                },
                csv: {
                    columns: this.getBaseColumnsLayout([...columns, ...customColumns]),
                    properties: {
                        encoding: 'ISO_8859_1',
                    },
                },
                columns: this.getBaseColumnsLayout([...columns, ...customColumns]),
            },
        };
    }

    getConfigs() {
        return {
            configAlterar: this.#configAlterar,
            configExcluir: this.#configExcluir,
            configExportar: this.#configExportar,
            configGeral: this.#configGeral,
            columnsTable: this.#getColumnsTable(),
        };
    }
}
